// React
import React from 'react';

// Component & Hooks
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Hidden from '@material-ui/core/Hidden';
import Divider from '@material-ui/core/Divider';
import { ADGMAlert, ADGMImage } from 'aurora-components';

// Utils
import { keyEmptyCheck, getFileIconName } from 'aurora-utils/utils';
import PropTypes from 'prop-types';

// Styles
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

const DocumentsUpload = ({
  classes,
  isPanel,
  attributes,
  documents,
  handleUpload,
  handleFileRemove,
  errorAttribute,
  hideInfoBox,
}) => {
  const getDeleteBtn = file => {
    return (
      <Button
        variant='outlined'
        onClick={() => handleFileRemove(file)}
        className={classNames(classes.fileActions, classes.delBtn)}
      >
        <ADGMImage name='Trash.svg' alt='tash-icon' />
      </Button>
    );
  };

  return (
    <div className={classes.fileUploadContainer}>
      {(attributes || [])?.map((attr, index) => {
        if (attr?.active && attr.attributeType.toLowerCase() === 'file') {
          // Documents as per section/field uploaded
          const currentSectionDocs = documents?.filter(doc => {
            return (
              doc.type === attr.name &&
              (attr?.subType ? doc.subType === attr.subType : true) &&
              (doc.userId ? +doc.userId === +attr.userId : doc.userId === null || typeof doc.userId === 'undefined') &&
              (doc.customerId
                ? +doc.customerId === +attr.customerId
                : doc.customerId === null || typeof doc.customerId === 'undefined') &&
              (doc.companyId
                ? +doc.companyId === +attr.companyId
                : doc.companyId === null || typeof doc.companyId === 'undefined')
            );
          });

          const shouldShowUploadButton =
            (Array.isArray(currentSectionDocs) ? currentSectionDocs?.length : 1) < (attr?.numberFilesToUpload || 3);
          const shouldShowSingleFileContent = !attr?.isMultiple && currentSectionDocs?.length > 0;
          const shouldShowExtraChild = attr.extraChild && attr.extraChild(currentSectionDocs);
          return (
            <>
              <Box
                className={classNames(isPanel ? classes.PanelDocBox : classes.docBox, {
                  [classes.docBoxError]: attr?.hasError,
                })}
                mb={{ xs: index === attributes.length - 1 ? 0 : 3, md: 3 }}
                p={[1.4, 1.8]}
                key={`${attr.name}-${index + 1}`}
              >
                <Box
                  className={classNames(isPanel && classes.PanelDocHeader)}
                  display={{ xs: 'block', md: 'flex' }}
                  justifyContent='space-between'
                  marginBottom={shouldShowUploadButton && !isEmpty(currentSectionDocs) && [2, 2, 0]}
                >
                  <Box
                    display='flex'
                    alignItems='center'
                    width={{ xs: 'auto', md: '70%', lg: 'auto' }}
                    marginBottom={{ xs: '12px', md: 0 }}
                  >
                    <Box
                      display='flex'
                      justifyContent='center'
                      alignItems='center'
                      className={classNames(
                        classes.docIcon,
                        shouldShowSingleFileContent ? classes.pdfUploaded : classes.docUploadIcon,
                      )}
                    >
                      <ADGMImage
                        name={
                          shouldShowSingleFileContent
                            ? getFileIconName(currentSectionDocs[currentSectionDocs.length - 1]?.fileName) ?? 'PDF.svg'
                            : 'doc-paper.svg'
                        }
                        alt='icon'
                      />
                    </Box>
                    <Box width={{ xs: 'auto', md: '70%', lg: 'auto' }}>
                      <Typography className={classes.docName}>
                        <span>{keyEmptyCheck(attr.title)}</span>
                        {attr?.mandatory && <span className={classes.mandatory}>*</span>}
                        {attr?.customBadge && attr?.customBadge(currentSectionDocs)}
                      </Typography>
                      {attr?.impMessage && (
                        <Hidden smDown>
                          <Typography className={classes.impMessage}>{keyEmptyCheck(attr?.impMessage)}</Typography>
                        </Hidden>
                      )}
                      {errorAttribute?.id === attr?.id && errorAttribute?.error && (
                        <Typography className={classes.errorAttribute}>
                          {keyEmptyCheck(errorAttribute?.error)}
                        </Typography>
                      )}

                      {shouldShowSingleFileContent && currentSectionDocs[currentSectionDocs.length - 1]?.fileName && (
                        <>
                          <Typography className={classes.uploadedFileName}>
                            {currentSectionDocs[currentSectionDocs.length - 1]?.fileName}
                            <ADGMImage name='success.svg' alt='icon' className={classes.fileSuccessIcon} />
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {attr?.impMessage && (
                    <Hidden mdUp>
                      <Typography className={classes.impMessage}>{keyEmptyCheck(attr?.impMessage)}</Typography>
                    </Hidden>
                  )}

                  <Box className={classes.fileActionsContainer} display='flex' alignItems={{ md: 'center' }}>
                    {attr?.extraActionComponent}
                    <label htmlFor={attr.id}>
                      {shouldShowUploadButton && (
                        <input
                          id={attr.id}
                          name={attr.name}
                          type='file'
                          accept={attr.acceptFileTypes}
                          multiple={attr.isMultiple}
                          hidden
                          data-user-id={attr?.userId}
                          data-company-id={attr?.companyId}
                          data-customer-id={attr?.customerId}
                          onChange={e => handleUpload(e, attr?.isMultiple, attr.id, attr.acceptFileTypes)}
                          onClick={e => {
                            e.target.value = null;
                          }}
                        />
                      )}
                      <Button
                        variant='outlined'
                        component='span'
                        id={attr.id}
                        name={attr.name}
                        className={shouldShowUploadButton ? classes.fileActions : classes.disableFileActions}
                        disabled={!shouldShowUploadButton}
                      >
                        {shouldShowUploadButton ? (
                          <svg
                            width='14'
                            height='14'
                            viewBox='0 0 14 14'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <g clipPath='url(#clip0_18142_39278)'>
                              <path
                                d='M13.4168 10.6289V11.7448C13.4167 12.1886 13.2403 12.6142 12.9265 12.9281C12.6127 13.2419 12.1871 13.4183 11.7432 13.4184H2.25708C1.81322 13.4184 1.38754 13.2421 1.07368 12.9282C0.75982 12.6144 0.583496 12.1887 0.583496 11.7448V10.6289'
                                stroke='#0088FF'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                              <path
                                d='M7 0.582031V10.5617'
                                stroke='#0088FF'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                              <path
                                d='M2.9165 4.66536L6.99984 0.582031L11.0832 4.66536'
                                stroke='#0088FF'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </g>
                            <defs>
                              <clipPath id='clip0_18142_39278'>
                                <rect width='14' height='14' fill='white' />
                              </clipPath>
                            </defs>
                          </svg>
                        ) : (
                          <svg
                            width='14'
                            height='14'
                            viewBox='0 0 14 14'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <g clipPath='url(#clip0_1887_30560)'>
                              <path
                                d='M4.52676 3.87959L6.14894 2.25684L6.16119 10.2085C6.16119 10.6917 6.55293 11.0835 7.03616 11.0835C7.51938 11.0835 7.91112 10.6917 7.91112 10.2085L7.89887 2.26676L9.51173 3.87962C9.84744 4.2272 10.4013 4.23682 10.7489 3.90111C11.0965 3.5654 11.1061 3.01149 10.7704 2.66392C10.7634 2.65661 10.7562 2.64945 10.7489 2.64242L8.87533 0.768829C7.85025 -0.256272 6.18826 -0.256272 5.16316 0.768801L5.16313 0.768829L3.28957 2.6424C2.95385 2.98998 2.96348 3.54388 3.31106 3.87959C3.65013 4.20708 4.18769 4.20708 4.52676 3.87959Z'
                                fill='#CED1D6'
                              />
                              <path
                                d='M13.1249 8.45856C12.6417 8.45856 12.25 8.85029 12.25 9.33352V12.0115C12.2496 12.1431 12.143 12.2497 12.0114 12.25H1.98898C1.85735 12.2497 1.75071 12.1431 1.75041 12.0115V9.33352C1.75041 8.85029 1.35868 8.45856 0.875451 8.45856C0.392225 8.45856 0.000488281 8.85029 0.000488281 9.33352V12.0115C0.00177338 13.1092 0.891309 13.9987 1.98898 14H12.0114C13.1091 13.9987 13.9986 13.1091 13.9999 12.0115V9.33352C13.9999 8.85029 13.6081 8.45856 13.1249 8.45856Z'
                                fill='#CED1D6'
                              />
                            </g>
                            <defs>
                              <clipPath id='clip0_1887_30560'>
                                <rect width='14' height='14' fill='white' />
                              </clipPath>
                            </defs>
                          </svg>
                        )}

                        <Typography
                          className={shouldShowUploadButton ? classes.fileActions : classes.disableText}
                          component='p'
                        >
                          {shouldShowSingleFileContent ? keyEmptyCheck('re-upload') : keyEmptyCheck('upload')}
                        </Typography>
                      </Button>
                    </label>
                    {shouldShowSingleFileContent && (
                      <Button
                        variant='outlined'
                        onClick={() => handleFileRemove(currentSectionDocs[currentSectionDocs.length - 1])}
                        className={classNames(classes.fileActions, classes.delBtn)}
                      >
                        {/* <ADGMImage name='Delete-bold-icon.svg' alt='del-icon' />
                         */}
                        <svg width='21' height='21' viewBox='0 0 21 21' fill='none' xmlns='http://www.w3.org/2000/svg'>
                          <g clipPath='url(#clip0_2176_8049)'>
                            <mask id='path-1-inside-1_2176_8049' fill='white'>
                              <path d='M19.849 3.3335H15.6823V2.91683C15.6823 2.3643 15.4628 1.83439 15.0721 1.44369C14.6814 1.05299 14.1515 0.833496 13.599 0.833496L7.76562 0.833496C7.21309 0.833496 6.68319 1.05299 6.29249 1.44369C5.90178 1.83439 5.68229 2.3643 5.68229 2.91683V3.3335H1.51562V5.8335H3.18229V18.3335C3.18229 18.9965 3.44568 19.6324 3.91452 20.1013C4.38337 20.5701 5.01925 20.8335 5.68229 20.8335H15.6823C16.3453 20.8335 16.9812 20.5701 17.4501 20.1013C17.9189 19.6324 18.1823 18.9965 18.1823 18.3335V5.8335H19.849V3.3335ZM15.6823 18.3335H5.68229V5.8335H15.6823V18.3335Z' />
                            </mask>
                            <path
                              d='M19.849 3.3335H15.6823V2.91683C15.6823 2.3643 15.4628 1.83439 15.0721 1.44369C14.6814 1.05299 14.1515 0.833496 13.599 0.833496L7.76562 0.833496C7.21309 0.833496 6.68319 1.05299 6.29249 1.44369C5.90178 1.83439 5.68229 2.3643 5.68229 2.91683V3.3335H1.51562V5.8335H3.18229V18.3335C3.18229 18.9965 3.44568 19.6324 3.91452 20.1013C4.38337 20.5701 5.01925 20.8335 5.68229 20.8335H15.6823C16.3453 20.8335 16.9812 20.5701 17.4501 20.1013C17.9189 19.6324 18.1823 18.9965 18.1823 18.3335V5.8335H19.849V3.3335ZM15.6823 18.3335H5.68229V5.8335H15.6823V18.3335Z'
                              fill='#130F26'
                            />
                            <path
                              d='M19.849 3.3335H41.1823V-17.9998H19.849V3.3335ZM15.6823 3.3335H-5.65104V24.6668H15.6823V3.3335ZM5.68229 2.91683H-15.651H5.68229ZM5.68229 3.3335V24.6668H27.0156V3.3335H5.68229ZM1.51562 3.3335V-17.9998H-19.8177V3.3335H1.51562ZM1.51562 5.8335H-19.8177V27.1668H1.51562V5.8335ZM3.18229 5.8335H24.5156V-15.4998H3.18229V5.8335ZM3.18229 18.3335H-18.151H3.18229ZM5.68229 20.8335V42.1668V20.8335ZM15.6823 20.8335V42.1668V20.8335ZM18.1823 18.3335H39.5156H18.1823ZM18.1823 5.8335V-15.4998H-3.15104V5.8335H18.1823ZM19.849 5.8335V27.1668H41.1823V5.8335H19.849ZM15.6823 18.3335V39.6668H37.0156V18.3335H15.6823ZM5.68229 18.3335H-15.651V39.6668H5.68229V18.3335ZM5.68229 5.8335V-15.4998H-15.651V5.8335H5.68229ZM15.6823 5.8335H37.0156V-15.4998H15.6823V5.8335ZM19.849 -17.9998H15.6823V24.6668H19.849V-17.9998ZM37.0156 3.3335V2.91683H-5.65104V3.3335H37.0156ZM37.0156 2.91683C37.0156 -3.29366 34.5485 -9.24978 30.157 -13.6413L-0.0128495 16.5286C-3.62292 12.9186 -5.65104 8.02225 -5.65104 2.91683H37.0156ZM30.157 -13.6413C25.7656 -18.0327 19.8094 -20.4998 13.599 -20.4998V22.1668C8.49354 22.1668 3.59723 20.1387 -0.0128495 16.5286L30.157 -13.6413ZM13.599 -20.4998H7.76562V22.1668H13.599V-20.4998ZM7.76562 -20.4998C1.55514 -20.4998 -4.40098 -18.0327 -8.79246 -13.6413L21.3774 16.5286C17.7674 20.1387 12.871 22.1668 7.76562 22.1668V-20.4998ZM-8.79246 -13.6413C-13.1839 -9.24978 -15.651 -3.29366 -15.651 2.91683H27.0156C27.0156 8.02225 24.9875 12.9186 21.3774 16.5286L-8.79246 -13.6413ZM-15.651 2.91683V3.3335H27.0156V2.91683H-15.651ZM5.68229 -17.9998H1.51562V24.6668H5.68229V-17.9998ZM-19.8177 3.3335V5.8335H22.849V3.3335H-19.8177ZM1.51562 27.1668H3.18229V-15.4998H1.51562V27.1668ZM-18.151 5.8335V18.3335H24.5156V5.8335H-18.151ZM-18.151 18.3335C-18.151 24.6545 -15.64 30.7166 -11.1704 35.1862L18.9995 5.01632C22.5314 8.54825 24.5156 13.3386 24.5156 18.3335H-18.151ZM-11.1704 35.1862C-6.7008 39.6558 -0.638696 42.1668 5.68229 42.1668L5.68229 -0.499838C10.6772 -0.499838 15.4675 1.48438 18.9995 5.01632L-11.1704 35.1862ZM5.68229 42.1668H15.6823V-0.499838H5.68229V42.1668ZM15.6823 42.1668C22.0033 42.1668 28.0654 39.6558 32.535 35.1862L2.36511 5.01632C5.89705 1.48438 10.6874 -0.499838 15.6823 -0.499838V42.1668ZM32.535 35.1862C37.0046 30.7166 39.5156 24.6545 39.5156 18.3335H-3.15104C-3.15104 13.3386 -1.16683 8.54826 2.36511 5.01632L32.535 35.1862ZM39.5156 18.3335V5.8335H-3.15104V18.3335H39.5156ZM18.1823 27.1668H19.849V-15.4998H18.1823V27.1668ZM41.1823 5.8335V3.3335H-1.48438V5.8335H41.1823ZM15.6823 -2.99984H5.68229V39.6668H15.6823V-2.99984ZM27.0156 18.3335V5.8335H-15.651V18.3335H27.0156ZM5.68229 27.1668H15.6823V-15.4998H5.68229V27.1668ZM-5.65104 5.8335V18.3335H37.0156V5.8335H-5.65104Z'
                              fill='#130F26'
                              mask='url(#path-1-inside-1_2176_8049)'
                            />
                            <path d='M9.84961 8.3335H7.34961V15.8335H9.84961V8.3335Z' fill='black' />
                            <path d='M14.0156 8.3335H11.5156V15.8335H14.0156V8.3335Z' fill='black' />
                          </g>
                          <defs>
                            <clipPath id='clip0_2176_8049'>
                              <rect width='20' height='20' fill='white' transform='translate(0.682617 0.833496)' />
                            </clipPath>
                          </defs>
                        </svg>
                      </Button>
                    )}
                  </Box>
                </Box>
                {shouldShowExtraChild && (
                  <>
                    <Divider className={classes.divider} />
                    {shouldShowExtraChild}
                  </>
                )}

                {attr?.isMultiple && !isPanel && currentSectionDocs?.length > 0 && (
                  <Divider className={classes.divider} />
                )}

                {attr?.isMultiple &&
                  currentSectionDocs?.map(file => (
                    <Box
                      display='flex'
                      justifyContent='space-between'
                      alignItems='center'
                      className={classes.removeDoc}
                      mt={1}
                      key={file.id}
                    >
                      <Box display='flex' alignItems='center'>
                        <Box
                          display='flex'
                          justifyContent='center'
                          alignItems='center'
                          className={classNames(classes.docIcon, classes.pdfUploaded)}
                        >
                          <ADGMImage name={getFileIconName(file.fileName, true)} width='16.67px' alt='icon' />
                        </Box>
                        <Typography component='p'>
                          {file.fileName}
                          <ADGMImage name='success.svg' alt='icon' className={classes.fileSuccessIcon} />
                        </Typography>
                      </Box>
                      {attr.shouldHideDeleteBtn?.(file) ? null : getDeleteBtn(file)}
                    </Box>
                  ))}

                {/* in case there is a hint msg */}
                {attr.message && (
                  <Box mb={{ xs: 0, md: 1.5 }} mt={{ xs: 2 }}>
                    <ADGMAlert type='mute' className='fullWidth' smoothBorderRaduis>
                      {keyEmptyCheck(attr.message)}
                    </ADGMAlert>
                  </Box>
                )}
              </Box>
              <Box className={classes.errMsg}>{attr?.errMsg && keyEmptyCheck(attr?.errMsg)}</Box>
            </>
          );
        }
        return null;
      })}
      {!hideInfoBox && (
        <ADGMAlert type='info' visible className={classes.infoPanel}>
          <div className={classes.infoSection}>
            <div>
              <span className={classes.bold}>{keyEmptyCheck('documents')}:</span>{' '}
              {keyEmptyCheck('documents_validation_msg')}
            </div>
            <div>
              <span className={classes.bold}>{keyEmptyCheck('photos')}:</span> {keyEmptyCheck('photo_validation_msg')}
            </div>
          </div>
        </ADGMAlert>
      )}
    </div>
  );
};

DocumentsUpload.propTypes = {
  classes: PropTypes.object.isRequired,
  documents: PropTypes.array.isRequired,
  attributes: PropTypes.array.isRequired,
  errorAttribute: PropTypes.array.isRequired,
  handleUpload: PropTypes.func.isRequired,
  handleFileRemove: PropTypes.func.isRequired,
  hideInfoBox: PropTypes.bool.isRequired,
  isPanel: PropTypes.bool,
};

DocumentsUpload.defaultProps = {
  isPanel: false,
};

export default DocumentsUpload;
